import React, { Component } from 'react'
import { Button, Form, FormInstance, Input, Select } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import { DEFAULT_SELECT_FILTER_OPTION } from 'helpers/form-fields.helper'

export type ExternalOption = {
  label: string
  value: string
  name: string
}

/** Resolves the external option list for a given integration. Should throw on failure. */
export type LoadExternalList = (integrationUuid: string) => Promise<ExternalOption[]>

type Props = {
  form: FormInstance
  integrationUuid: string
  /** Loads the selectable external options (e.g. colors, locations, countries, states). */
  loadExternalList: LoadExternalList
  /** Label for the external Form.Item. Defaults to "External". */
  label?: string
  /** Placeholder for the external Select. Defaults to "Select an option". */
  placeholder?: string
  /** Form field that stores the selected option's id. Defaults to "external_id". */
  name?: string
  /** Form field that stores the selected option's display name. Pass null to skip storing it. Defaults to "name". */
  nameField?: string | null
  /** When this value changes the list reloads (e.g. a parent country selection that filters the options). */
  reloadKey?: string | number
  /** Disables the select and skips auto-loading (e.g. a prerequisite selection isn't ready yet). The current value is kept visible. */
  disabled?: boolean
  /** Called when the user picks an option (not on programmatic reloads). */
  onChange?: (value: string, option: ExternalOption) => void
}

type State = {
  fetching: boolean
  externalList: ExternalOption[] | undefined
  loadedKey: string | undefined
  error: string | undefined
}

/**
 * Generic Shelter Buddy external picker. Loads the option list for the selected
 * integration via the injected `loadExternalList`, stores the chosen option's id
 * in `name` (default "external_id") and its display name in `nameField` (default
 * "name"). Reuse across mapping forms (color, branch, state, ...) by passing a
 * different loader. Chained pickers (e.g. country → state) can pass `reloadKey`
 * to reload the list whenever the parent selection changes.
 */
export default class ShelterBuddyExternalField extends Component<Props, State> {
  state: State = {
    fetching: false,
    externalList: undefined,
    loadedKey: undefined,
    error: undefined,
  }

  /** Identifies the data currently loaded — integration plus the optional reload dependency. */
  get currentKey() {
    return `${this.props.integrationUuid}|${this.props.reloadKey ?? ''}`
  }

  componentDidMount() {
    this.loadIfNeeded()
  }

  componentDidUpdate(prevProps: Props) {
    if (
      prevProps.integrationUuid !== this.props.integrationUuid ||
      prevProps.reloadKey !== this.props.reloadKey ||
      // Became enabled — load the list we deferred while disabled.
      (prevProps.disabled && !this.props.disabled)
    ) {
      this.loadIfNeeded()
    }
  }

  loadIfNeeded() {
    const { integrationUuid, disabled } = this.props
    const { fetching, loadedKey, error } = this.state

    if (disabled || fetching || !integrationUuid) return
    if (this.currentKey === loadedKey && !error) return

    this.loadExternal()
  }

  loadExternal = () => {
    const { integrationUuid, name = 'external_id' } = this.props

    this.setState({ fetching: true, loadedKey: this.currentKey, error: undefined }, async () => {
      try {
        const externalList = await this.props.loadExternalList(integrationUuid)

        this.setState({ fetching: false, externalList, error: undefined }, () => {
          // antd Select won't refresh the displayed label when options arrive
          // after the value is set — bouncing the value through setFieldsValue
          // forces the label to resolve.
          const value = this.props.form.getFieldValue(name)?.toString()
          this.props.form.setFieldsValue({ [name]: value })
        })
      } catch (e) {
        console.error(e)
        const message = (e as Error)?.message || 'Failed to load external list'
        this.setState({ fetching: false, externalList: undefined, error: message })
      }
    })
  }

  handleRetry = () => {
    if (!this.props.integrationUuid) return
    this.loadExternal()
  }

  render() {
    const {
      form,
      label = 'External',
      placeholder = 'Select an option',
      name = 'external_id',
      nameField = 'name',
      disabled = false,
      onChange,
    } = this.props
    const { fetching, externalList, error } = this.state

    // Before the list loads (e.g. while disabled on an edit), keep the saved
    // value readable by seeding a single option from the current form values.
    const currentValue = form.getFieldValue(name)?.toString()
    const currentName = nameField ? form.getFieldValue(nameField) : undefined
    const fallbackOptions: ExternalOption[] | undefined =
      currentValue
        ? [{ value: currentValue, name: currentName ?? '', label: currentName ? `${currentName} [${currentValue}]` : currentValue }]
        : undefined
    const options = externalList ?? fallbackOptions

    const help = error ? (
      <span>
        {error}{' '}
        <Button
          size="small"
          type="link"
          icon={<ReloadOutlined />}
          loading={fetching}
          onClick={this.handleRetry}
        >
          Retry
        </Button>
      </span>
    ) : undefined

    return (
      <>
        <Form.Item
          label={label}
          name={name}
          validateStatus={error ? 'error' : undefined}
          help={help}
          rules={[{ required: true, message: 'Missing external mapping' }]}
        >
          <Select
            loading={fetching}
            disabled={disabled}
            placeholder={placeholder}
            options={options}
            showSearch
            filterOption={DEFAULT_SELECT_FILTER_OPTION}
            onSelect={(value, option) => {
              const picked = option as unknown as ExternalOption
              if (nameField) {
                form.setFieldsValue({ [nameField]: picked.name })
              }
              if (onChange) {
                onChange(value as string, picked)
              }
            }}
          />
        </Form.Item>
        {nameField ? (
          <Form.Item
            hidden
            label="Name"
            name={nameField}
            rules={[{ required: true, message: 'Missing external mapping' }]}
          >
            <Input />
          </Form.Item>
        ) : null}
      </>
    )
  }
}
